/**
 * Razorpay Payment Gateway Service
 * 
 * This service provides a reusable interface for Razorpay payment integration.
 * It can be easily replaced with another payment gateway by implementing the same interface.
 * 
 * Features:
 * - Payment order creation
 * - Payment processing with Razorpay Checkout
 * - Payment verification
 * - Support for UPI and Card payments
 */

import { supabase } from '../lib/supabase';

// Load Razorpay script dynamically
const loadRazorpayScript = () => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.Razorpay) {
      console.log('✅ Razorpay script already loaded');
      resolve(true);
      return;
    }

    console.log('📥 Loading Razorpay script...');
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;

    script.onload = () => {
      console.log('✅ Razorpay script loaded successfully');
      // Double check that Razorpay is available
      if (window.Razorpay) {
        resolve(true);
      } else {
        console.error('❌ Razorpay script loaded but window.Razorpay is undefined');
        reject(new Error('Razorpay SDK failed to initialize'));
      }
    };

    script.onerror = (error) => {
      console.error('❌ Failed to load Razorpay script:', error);
      reject(new Error('Failed to load Razorpay SDK. Please check your internet connection.'));
    };

    document.body.appendChild(script);
  });
};

export const razorpayService = {
  /**
   * Initialize Razorpay (load script if needed)
   */
  async initialize() {
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        throw new Error('Failed to load Razorpay SDK');
      }
      return true;
    } catch (error) {
      console.error('❌ Razorpay initialization failed:', error);
      throw error;
    }
  },

  /**
   * Create a payment order via backend
   * @param {Object} orderData - Order details
   * @returns {Promise<Object>} Order details with order_id
   */
  async createOrder(orderData) {
    try {
      console.log('💳 Creating Razorpay order:', orderData);

      // Call Supabase Edge Function to create order
      const { data, error } = await supabase.functions.invoke('create-razorpay-order', {
        body: {
          amount: orderData.amount, // Amount in rupees (will be converted to paise in backend)
          currency: orderData.currency || 'INR',
          receipt: orderData.receipt || `receipt_${Date.now()}`,
          notes: orderData.notes || {}
        }
      });

      if (error) {
        console.error('❌ Error creating Razorpay order:', error);
        throw new Error(error.message || 'Failed to create payment order');
      }

      console.log('✅ Razorpay order created:', data);
      return data;
    } catch (error) {
      console.error('❌ Exception in createOrder:', error);
      throw error;
    }
  },

  /**
   * Open Razorpay payment checkout
   * @param {Object} options - Payment options
   * @returns {Promise<Object>} Payment response
   */
  async processPayment(options) {
    try {
      // Ensure Razorpay is loaded
      console.log('🔄 Initializing Razorpay...');
      await this.initialize();
      console.log('✅ Razorpay initialized');

      // Check if Razorpay is available
      if (!window.Razorpay) {
        throw new Error('Razorpay SDK not loaded. Please refresh the page and try again.');
      }

      const {
        orderId,
        amount,
        currency = 'INR',
        name = 'Ayur Parishkar Kendra',
        description = 'Appointment Booking',
        image = '/assets/images/sdapk-round.png',
        prefill = {},
        theme = { color: '#2F8A4C' }
      } = options;

      // Validate required parameters
      if (!orderId) {
        throw new Error('Order ID is required');
      }
      if (!amount || amount <= 0) {
        throw new Error('Invalid payment amount');
      }

      const razorpayKeyId = import.meta.env.VITE_RAZORPAY_KEY_ID;
      if (!razorpayKeyId) {
        throw new Error('Razorpay Key ID not configured. Please contact support.');
      }

      console.log('💳 Opening Razorpay checkout:', {
        orderId,
        amount,
        keyId: razorpayKeyId.substring(0, 15) + '...' // Log partial key for debugging
      });

      return new Promise((resolve, reject) => {
        try {
          const razorpayOptions = {
            key: razorpayKeyId,
            amount: Math.round(amount * 100), // Convert to paise
            currency: currency,
            name: name,
            description: description,
            image: image,
            order_id: orderId,
            prefill: {
              name: prefill.name || '',
              email: prefill.email || '',
              contact: prefill.contact || ''
            },
            theme: theme,
            modal: {
              ondismiss: () => {
                console.log('⚠️ Payment modal dismissed by user');
                reject(new Error('Payment cancelled by user'));
              },
              escape: true,
              backdropclose: false
            },
            handler: (response) => {
              console.log('✅ Payment successful:', response);
              resolve({
                success: true,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature
              });
            }
          };

          console.log('🔧 Creating Razorpay instance with options:', {
            ...razorpayOptions,
            key: razorpayOptions.key.substring(0, 15) + '...'
          });

          const razorpay = new window.Razorpay(razorpayOptions);

          razorpay.on('payment.failed', (response) => {
            console.error('❌ Payment failed:', response.error);
            reject(new Error(response.error.description || 'Payment failed'));
          });

          console.log('🚀 Opening Razorpay modal...');
          razorpay.open();
          console.log('✅ Razorpay modal opened');
        } catch (err) {
          console.error('❌ Error creating/opening Razorpay:', err);
          reject(err);
        }
      });
    } catch (error) {
      console.error('❌ Exception in processPayment:', error);
      throw error;
    }
  },

  /**
   * Verify payment signature via backend
   * @param {Object} paymentData - Payment verification data
   * @returns {Promise<Object>} Verification result
   */
  async verifyPayment(paymentData) {
    try {
      console.log('🔍 Verifying payment:', paymentData);

      // Call Supabase Edge Function to verify payment
      const { data, error } = await supabase.functions.invoke('verify-razorpay-payment', {
        body: paymentData
      });

      if (error) {
        console.error('❌ Error verifying payment:', error);
        throw new Error(error.message || 'Payment verification failed');
      }

      console.log('✅ Payment verified:', data);
      return data;
    } catch (error) {
      console.error('❌ Exception in verifyPayment:', error);
      throw error;
    }
  }
};

